/* ==========================================================================
   BAAGHI — global.js
   Reveal animations · drawers · header · accordions · quantity · search
   ========================================================================== */
(function () {
  'use strict';

  const root = document.documentElement;
  const body = document.body;

  /* ── Utilities ────────────────────────────────────────────────────────── */

  const focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function debounce(fn, wait) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, arguments), wait);
    };
  }

  window.BAAGHI = window.BAAGHI || {};

  window.BAAGHI.formatMoney = function (cents) {
    const format = (window.BAAGHI.moneyFormat || '${{amount}}').toString();
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    const withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return format
      .replace(/\{\{\s*amount\s*\}\}/, withCommas + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  };

  let toastTimer;
  window.BAAGHI.toast = function (message) {
    const toast = document.getElementById('Toast');
    if (!toast || !message) return;
    toast.querySelector('.toast__text').textContent = message;
    toast.hidden = false;
    requestAnimationFrame(() => toast.classList.add('is-open'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toast.classList.remove('is-open');
      setTimeout(() => (toast.hidden = true), 400);
    }, 3200);
  };

  /* ── Scroll reveal ────────────────────────────────────────────────────── */

  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, obs) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-visible');
              obs.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
        )
      : null;

  function observeReveals(scope) {
    (scope || document).querySelectorAll('.reveal:not(.is-visible), .reveal-mask:not(.is-visible)').forEach((el) => {
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('is-visible');
    });
  }
  window.BAAGHI.observeReveals = observeReveals;

  /* ── Overlay + body lock ──────────────────────────────────────────────── */

  const overlay = document.querySelector('[data-overlay]');
  let openPanels = 0;
  let scrollLockY = 0;

  function lockScroll() {
    if (openPanels === 0) {
      scrollLockY = window.scrollY;
      body.style.top = `-${scrollLockY}px`;
      body.classList.add('is-locked');
      body.style.position = 'fixed';
      body.style.width = '100%';
    }
    openPanels += 1;
  }

  function unlockScroll() {
    openPanels = Math.max(0, openPanels - 1);
    if (openPanels === 0) {
      body.classList.remove('is-locked');
      body.style.position = '';
      body.style.top = '';
      body.style.width = '';
      window.scrollTo(0, scrollLockY);
    }
  }

  function showOverlay() {
    if (!overlay) return;
    overlay.hidden = false;
    requestAnimationFrame(() => overlay.classList.add('is-open'));
  }

  function hideOverlay() {
    if (!overlay || openPanels > 0) return;
    overlay.classList.remove('is-open');
    setTimeout(() => {
      if (openPanels === 0) overlay.hidden = true;
    }, 320);
  }

  /* ── Drawer controller ────────────────────────────────────────────────── */

  const Drawer = {
    open(id, trigger) {
      const drawer = document.getElementById(id);
      if (!drawer || drawer.classList.contains('is-open')) return;
      drawer.hidden = false;
      drawer.dataset.returnFocus = trigger ? trigger.id || '' : '';
      requestAnimationFrame(() => drawer.classList.add('is-open'));
      lockScroll();
      showOverlay();
      drawer.setAttribute('aria-hidden', 'false');
      const target = drawer.querySelector('[data-drawer-autofocus]') || drawer.querySelector(focusableSelector);
      if (target) setTimeout(() => target.focus({ preventScroll: true }), 120);
      document.dispatchEvent(new CustomEvent('baaghi:drawer:open', { detail: { id } }));
    },
    close(id) {
      const drawer = id ? document.getElementById(id) : document.querySelector('.drawer.is-open');
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      unlockScroll();
      hideOverlay();
      const returnId = drawer.dataset.returnFocus;
      if (returnId) {
        const el = document.getElementById(returnId);
        if (el) el.focus({ preventScroll: true });
      }
      setTimeout(() => {
        if (!drawer.classList.contains('is-open')) drawer.hidden = true;
      }, 520);
    },
    closeAll() {
      document.querySelectorAll('.drawer.is-open').forEach((d) => Drawer.close(d.id));
    }
  };
  window.BAAGHI.Drawer = Drawer;

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      Drawer.open(opener.getAttribute('data-drawer-open'), opener);
      return;
    }
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      event.preventDefault();
      Drawer.close(closer.getAttribute('data-drawer-close') || closer.closest('.drawer')?.id);
    }
  });

  if (overlay) overlay.addEventListener('click', () => Drawer.closeAll());

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('.drawer.is-open');
    if (open) Drawer.close(open.id);
  });

  // Focus trap
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const drawer = document.querySelector('.drawer.is-open');
    if (!drawer) return;
    const items = Array.from(drawer.querySelectorAll(focusableSelector)).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ── Header behaviour ─────────────────────────────────────────────────── */

  const headerWrap = document.querySelector('[data-header]');
  if (headerWrap) {
    const setHeight = () => {
      const h = headerWrap.querySelector('.header');
      if (h) root.style.setProperty('--header-height', `${h.offsetHeight}px`);
    };
    setHeight();
    window.addEventListener('resize', debounce(setHeight, 150));

    let lastY = window.scrollY;
    const hideOnScroll = headerWrap.hasAttribute('data-hide-on-scroll');

    const onScroll = () => {
      const y = window.scrollY;
      headerWrap.classList.toggle('is-scrolled', y > 12);
      if (hideOnScroll && !document.querySelector('.drawer.is-open')) {
        const goingDown = y > lastY && y > 240;
        headerWrap.classList.toggle('is-hidden', goingDown);
      }
      lastY = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  const headerDropdowns = Array.from(document.querySelectorAll('[data-header-dropdown]'));
  const closeHeaderDropdowns = (exception) => {
    headerDropdowns.forEach((item) => {
      if (item === exception) return;
      item.classList.remove('is-open');
      const toggle = item.querySelector('[data-header-dropdown-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-header-dropdown-toggle]');
    if (!toggle) {
      if (!event.target.closest('[data-header-dropdown]')) closeHeaderDropdowns();
      return;
    }

    const item = toggle.closest('[data-header-dropdown]');
    const open = item.classList.contains('is-open');
    closeHeaderDropdowns(item);
    item.classList.toggle('is-open', !open);
    // closing by click must also beat the hover rule while the pointer is still on the item
    item.classList.toggle('is-dismissed', open);
    toggle.setAttribute('aria-expanded', String(!open));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    // a hover-opened panel has no .is-open, but Escape must still dismiss it
    headerDropdowns.forEach((item) => { if (item.matches(':hover')) item.classList.add('is-dismissed'); });
    const openItem = document.querySelector('[data-header-dropdown].is-open');
    if (!openItem) return;
    const toggle = openItem.querySelector('[data-header-dropdown-toggle]');
    closeHeaderDropdowns();
    if (toggle) toggle.focus();
  });

  headerDropdowns.forEach((item) => {
    // a click-opened menu must not stay pinned open under the next hovered one
    item.addEventListener('mouseleave', () => {
      item.classList.remove('is-dismissed');
      if (item.classList.contains('is-open')) closeHeaderDropdowns();
    });
    item.addEventListener('focusout', (event) => {
      if (item.classList.contains('is-open') && !item.contains(event.relatedTarget)) closeHeaderDropdowns();
    });
  });

  // closed mega panels are display:none, so their lazy card images would only start loading on open
  const headerMenu = document.querySelector('[data-header-menu]');
  if (headerMenu) {
    const warmMegaImages = () => {
      headerMenu.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    };
    headerMenu.addEventListener('pointerenter', warmMegaImages, { once: true });
    headerMenu.addEventListener('focusin', warmMegaImages, { once: true });
  }

  /* ── Mobile nav accordions ────────────────────────────────────────────── */

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-nav-toggle]');
    if (!toggle) return;
    event.preventDefault();
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;
    const open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!open));
    panel.dataset.open = String(!open);
  });

  /* ── Announcement rotator ─────────────────────────────────────────────── */

  document.querySelectorAll('[data-announcement]').forEach((bar) => {
    const slides = Array.from(bar.querySelectorAll('.announcement__slide'));
    if (slides.length < 2) return;
    let index = 0;
    const speed = parseInt(bar.dataset.speed, 10) || 5000;
    const go = (next) => {
      slides[index].classList.remove('is-active');
      index = (next + slides.length) % slides.length;
      slides[index].classList.add('is-active');
    };
    let timer = setInterval(() => go(index + 1), speed);
    bar.querySelectorAll('[data-announcement-prev]').forEach((b) =>
      b.addEventListener('click', () => {
        clearInterval(timer);
        go(index - 1);
        timer = setInterval(() => go(index + 1), speed);
      })
    );
    bar.querySelectorAll('[data-announcement-next]').forEach((b) =>
      b.addEventListener('click', () => {
        clearInterval(timer);
        go(index + 1);
        timer = setInterval(() => go(index + 1), speed);
      })
    );
  });

  /* ── Quantity inputs ──────────────────────────────────────────────────── */

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-qty-change]');
    if (!btn) return;
    event.preventDefault();
    const wrap = btn.closest('[data-qty]');
    const input = wrap && wrap.querySelector('input');
    if (!input) return;
    const step = btn.getAttribute('data-qty-change') === 'up' ? 1 : -1;
    const min = parseInt(input.min, 10) || 0;
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    const next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || 0) + step));
    if (next === parseInt(input.value, 10)) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ── Exclusive-open accordions ────────────────────────────────────────── */

  document.querySelectorAll('[data-accordion-exclusive]').forEach((group) => {
    group.addEventListener('toggle', (event) => {
      const item = event.target;
      if (item.tagName !== 'DETAILS' || !item.open) return;
      group.querySelectorAll('details[open]').forEach((other) => {
        if (other !== item) other.open = false;
      });
    }, true);
  });

  /* ── Predictive search ────────────────────────────────────────────────── */

  const searchForms = document.querySelectorAll('[data-predictive-search]');
  searchForms.forEach((form) => {
    const input = form.querySelector('input[type="search"]');
    const results = form.parentElement.querySelector('[data-predictive-results]');
    if (!input || !results) return;
    let controller;

    const run = debounce(() => {
      const term = input.value.trim();
      if (term.length < 2) {
        results.innerHTML = '';
        return;
      }
      if (controller) controller.abort();
      controller = new AbortController();
      const url = `${window.BAAGHI.routes.predictiveSearch}?q=${encodeURIComponent(
        term
      )}&resources[type]=product,collection,page&resources[limit]=6&section_id=predictive-search`;

      fetch(url, { signal: controller.signal })
        .then((res) => res.text())
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const markup = doc.querySelector('[data-predictive-inner]');
          results.innerHTML = markup ? markup.innerHTML : '';
        })
        .catch(() => {});
    }, 250);

    input.addEventListener('input', run);
  });

  /* ── Copy to clipboard ────────────────────────────────────────────────── */

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-copy]');
    if (!btn) return;
    event.preventDefault();
    const value = btn.getAttribute('data-copy');
    if (navigator.clipboard) {
      navigator.clipboard.writeText(value).then(() => window.BAAGHI.toast(btn.dataset.copyMessage || 'Copied'));
    }
  });

  /* ── External links (social, WhatsApp) ────────────────────────────────── */
  // New tab only with a desktop mouse. Phones and in-app browsers (Instagram, Facebook, WhatsApp)
  // often ignore target="_blank", so the tap did nothing; same-tab lets the app open instead.
  const desktopPointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const setExternalTargets = () => {
    document.querySelectorAll('a[data-external]').forEach((link) => {
      if (desktopPointer.matches) link.target = '_blank';
      else link.removeAttribute('target');
    });
  };
  setExternalTargets();
  if (desktopPointer.addEventListener) desktopPointer.addEventListener('change', setExternalTargets);
  document.addEventListener('shopify:section:load', setExternalTargets);

  /* ── Init ─────────────────────────────────────────────────────────────── */

  observeReveals(document);

  document.addEventListener('shopify:section:load', (event) => {
    observeReveals(event.target);
  });
})();
